/**
 * Schema.org JSON-LD builders (Story 30.4). Pure functions returning plain
 * objects; the <JsonLd> component serialises them. Entity facts come from
 * entity.ts; pricing from @provenleap/types PLAN_PRICING (never hardcoded).
 */
import { PLAN_DISPLAY_NAMES, PLAN_PRICING, type Plan } from "@provenleap/types";
import {
  FOUNDER_KNOWS_ABOUT,
  FOUNDER_NAME,
  FOUNDER_SAME_AS,
  ORG_DESCRIPTION,
  ORG_LOGO,
  ORG_NAME,
  ORG_SAME_AS,
  ORG_URL,
  realSameAs,
} from "./entity";

const SCHEMA_CONTEXT = "https://schema.org";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type JsonLdObject = Record<string, any>;

/** Organization — rendered on every page. `sameAs`/founder omitted when empty. */
export function organizationSchema(): JsonLdObject {
  const sameAs = realSameAs(ORG_SAME_AS);
  const founderSameAs = realSameAs(FOUNDER_SAME_AS);
  const org: JsonLdObject = {
    "@context": SCHEMA_CONTEXT,
    "@type": "Organization",
    name: ORG_NAME,
    url: ORG_URL,
    logo: ORG_LOGO,
    description: ORG_DESCRIPTION,
    founder: {
      "@type": "Person",
      name: FOUNDER_NAME,
      knowsAbout: FOUNDER_KNOWS_ABOUT,
      ...(founderSameAs.length > 0 ? { sameAs: founderSameAs } : {}),
    },
  };
  if (sameAs.length > 0) org.sameAs = sameAs;
  return org;
}

/** Plan tiers, cheapest → priciest, for the SoftwareApplication offers. */
const OFFER_PLANS: Plan[] = ["free", "starter", "pro", "agency"];

/** SoftwareApplication — homepage. Offers built from PLAN_PRICING (no drift). */
export function softwareApplicationSchema(competitorUrls: string[] = []): JsonLdObject {
  // A plain price/priceCurrency Offer is valid + Rich-Results-clean. (The
  // epic's example added `billingIncrement: "MON"`, but that is NOT a valid
  // schema.org Offer property — it belongs on UnitPriceSpecification as a
  // Number — so it is omitted rather than shipping invalid structured data.)
  const offers = OFFER_PLANS.map((plan) => ({
    "@type": "Offer",
    name: PLAN_DISPLAY_NAMES[plan],
    price: String(plan === "free" ? 0 : PLAN_PRICING[plan].monthly),
    priceCurrency: "USD",
  }));
  // NOTE: Google requires name + offers.price + one of aggregateRating/review
  // for SoftwareApplication RICH-RESULT eligibility. We intentionally omit the
  // rating — no genuine reviews exist yet, and fabricating an aggregateRating
  // would be worse than not being rich-result-eligible (same omit-not-fake
  // discipline as the `sameAs` placeholders). The block is still VALID schema
  // and feeds entity understanding; add a real aggregateRating once G2/Capterra
  // reviews exist (Story 30.24).
  const schema: JsonLdObject = {
    "@context": SCHEMA_CONTEXT,
    "@type": "SoftwareApplication",
    name: ORG_NAME,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: ORG_DESCRIPTION,
    url: ORG_URL,
    offers,
  };
  if (competitorUrls.length > 0) schema.isSimilarTo = competitorUrls;
  return schema;
}

export interface QA {
  question: string;
  answer: string;
}

/** FAQPage — built from the SAME data as the visible FAQ (single source). */
export function faqPageSchema(items: QA[]): JsonLdObject {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "FAQPage",
    mainEntity: items.map((qa) => ({
      "@type": "Question",
      name: qa.question,
      acceptedAnswer: { "@type": "Answer", text: qa.answer },
    })),
  };
}

export interface Breadcrumb {
  name: string;
  url: string;
}

/** BreadcrumbList — coordinates with Story 30.6. */
export function breadcrumbListSchema(crumbs: Breadcrumb[]): JsonLdObject {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };
}

export interface ArticleInput {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified: string;
  authorName: string;
  image?: string;
}

/** Article / BlogPosting — wired by Story 30.10. */
export function articleSchema(input: ArticleInput): JsonLdObject {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "BlogPosting",
    headline: input.headline,
    description: input.description,
    url: input.url,
    datePublished: input.datePublished,
    dateModified: input.dateModified,
    author: { "@type": "Person", name: input.authorName },
    publisher: {
      "@type": "Organization",
      name: ORG_NAME,
      logo: { "@type": "ImageObject", url: ORG_LOGO },
    },
    ...(input.image ? { image: input.image } : {}),
  };
}

/** ProfilePage — wired by Story 30.15 (About page). */
export function profilePageSchema(person: {
  name: string;
  description: string;
  url: string;
  sameAs?: string[];
  knowsAbout?: string[];
}): JsonLdObject {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "ProfilePage",
    mainEntity: {
      "@type": "Person",
      name: person.name,
      description: person.description,
      url: person.url,
      ...(person.knowsAbout ? { knowsAbout: person.knowsAbout } : {}),
      ...(person.sameAs && person.sameAs.length > 0 ? { sameAs: person.sameAs } : {}),
    },
  };
}

export interface DefinedTerm {
  name: string;
  description: string;
}

/** DefinedTermSet — glossary pages (Story 30.20). AI engines extract these preferentially. */
export function definedTermSetSchema(input: { name: string; description: string; terms: DefinedTerm[] }): JsonLdObject {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "DefinedTermSet",
    name: input.name,
    description: input.description,
    hasDefinedTerm: input.terms.map((t) => ({
      "@type": "DefinedTerm",
      name: t.name,
      description: t.description,
    })),
  };
}

export interface HowToStep {
  name: string;
  text: string;
}

/** HowTo — step-by-step pages (e.g. the MCP docs page, Story 30.13). */
export function howToSchema(input: { name: string; description: string; steps: HowToStep[] }): JsonLdObject {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

export interface DatasetInput {
  name: string;
  description: string;
  contentUrl: string;
  encodingFormat?: string;
}

/** Dataset — future benchmark/research pages (Google Dataset Search). */
export function datasetSchema(input: DatasetInput): JsonLdObject {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Dataset",
    name: input.name,
    description: input.description,
    distribution: {
      "@type": "DataDownload",
      encodingFormat: input.encodingFormat ?? "text/csv",
      contentUrl: input.contentUrl,
    },
  };
}
